/* eslint-disable no-useless-catch */
import {
  fetchProject,
  fetchProjectById,
  getBarcode,
  getBarcodeByPath,
  getBarcodeChildren,
  getBarcodeList,
  getProduct,
  undoTracking,
} from '@/utils/api'
import { formatSearchConditionBlocks } from '@/utils/helpers'
import cloneDeep from 'lodash/cloneDeep'
import {
  CLEAR_ALL,
  CLEAR_DETAIL_BARCODE,
  FETCH_BARCODE,
  FETCH_BARCODE_CHILDREN,
  LOAD_BARCODE_LIST,
  SAVE_BARCODE,
  UPDATE_BARCODE_LIST_COUNT,
  UNDO_TRACKING,
  UNDO_LINKING,
} from './actions'
import {
  IBarcodeList,
  IBarcodeDetail,
  IProject,
  IProduct,
  IBarcodeSearchForm,
  ISearchConditionBlock,
  IBarcodeSearch,
  IBarcodeResult,
  IBarcode,
} from '@/utils/types'
import { IManufacturingPlanSearch } from '@/utils/types/model/manufacturingPlan'
import { getCompiledBarcode } from '@/utils/barcodeHelper'

type TBarcodeStates = {
  isLoaded: boolean
  searchProjectCode: string
  searchProjectVersion: number
  totalBarcodes: number
  barcodes: Array<IManufacturingPlanSearch>
  barcode: IBarcodeDetail
  projectList: IProject
  projectDetail: IProject
  product: IProduct
  parentId: string
  barcodeChildren: Array<IBarcodeList>
  dataSearch: IBarcodeSearchForm
}

export default {
  state: {
    isLoaded: false,
    searchProjectCode: '',
    searchProjectVersion: 0,
    totalBarcodes: 0,
    barcodes: [],
    barcode: {} as IBarcodeDetail,
    projectList: {} as IProject,
    projectDetail: {} as IProject,
    product: {} as IProduct,
    dataSearch: {} as IBarcodeSearchForm,
  },
  getters: {
    projectParam: (state: TBarcodeStates) => {
      if (state?.projectDetail) {
        return `${state.projectDetail.code}@${state.projectDetail.version}`
      }
      return ''
    },
  },
  mutations: {
    [LOAD_BARCODE_LIST]: (
      state: TBarcodeStates,
      {
        barcodes,
        totalBarcodes,
        dataSearch,
        project,
      }: {
        barcodes: Array<IManufacturingPlanSearch>
        totalBarcodes: number
        dataSearch: IBarcodeSearchForm
        project?: IProject
      }
    ) => {
      state.isLoaded = true
      state.barcodes = barcodes
      state.totalBarcodes = totalBarcodes
      state.dataSearch = cloneDeep(dataSearch)
      if (project) {
        state.projectList = project
        state.searchProjectCode = project.code ?? ''
        state.searchProjectVersion = project.version ?? 0
      }
    },
    [UPDATE_BARCODE_LIST_COUNT]: (state: TBarcodeStates, { totalBarcodes }: { totalBarcodes: number }) => {
      state.totalBarcodes = totalBarcodes
    },
    [SAVE_BARCODE]: (
      state: TBarcodeStates,
      {
        barcode,
        project,
        product,
      }: {
        barcode: IBarcodeDetail
        project?: IProject
        product?: IProduct
      }
    ) => {
      state.barcode = { ...barcode }
      if (project) {
        state.projectDetail = project
      }
      if (product) {
        state.product = product
      }
    },
    [FETCH_BARCODE_CHILDREN]: (
      state: TBarcodeStates,
      {
        barcodeChildren,
        barcodeId,
      }: {
        barcodeChildren: Array<IBarcodeList>
        barcodeId: string
      }
    ) => {
      state.barcodeChildren = barcodeChildren
      state.parentId = barcodeId
    },
    [CLEAR_DETAIL_BARCODE]: (state: TBarcodeStates) => {
      state.barcode = {} as IBarcodeDetail
    },
    [CLEAR_ALL]: (state: TBarcodeStates) => {
      state.isLoaded = false
      state.searchProjectCode = ''
      state.barcodes = []
      state.barcode = {} as IBarcodeDetail
      state.projectList = {} as IProject
      state.projectDetail = {} as IProject
      state.product = {} as IProduct
    },
  },
  actions: {
    [LOAD_BARCODE_LIST]: async (
      {
        commit,
        state,
      }: {
        commit: Function
        state: TBarcodeStates
      },
      data: IBarcodeSearchForm
    ) => {
      try {
        let project

        if (
          !(
            state.projectList &&
            data.projectCode === state.searchProjectCode &&
            data.version === state.searchProjectVersion
          )
        ) {
          project = await fetchProject(data.projectCode, data.version === 0 ? undefined : String(data.version))
        } else {
          project = state.projectList
        }

        // format dataSearch
        const condBlocks = [] as ISearchConditionBlock[]
        data.searchConditionBlocks.forEach((i) => condBlocks.push(cloneDeep(i)))
        const dataSearch = {
          condition: {
            ...data,
            searchConditionBlocks: formatSearchConditionBlocks(condBlocks),
          },
        } as IBarcodeSearch
        let result = {} as IBarcodeResult
        try {
          const response = await getBarcodeList(dataSearch)
          result = {
            count: response?.totalRecords ?? 0,
            results: response?.manufacturingPlans ?? [],
          }
        } catch (e) {
          result = { count: 0, results: [] }
        }
        commit(LOAD_BARCODE_LIST, {
          barcodes: result.results,
          dataSearch: data,
          totalBarcodes: result.count,
          project: project,
        })
      } catch (err) {
        throw err
      }
    },
    [FETCH_BARCODE]: async (
      {
        commit,
        state,
      }: {
        commit: Function
        state: TBarcodeStates
      },
      data: {
        id: string
        path?: string
      }
    ) => {
      try {
        let barcode: IBarcode
        if (data.path) {
          barcode = await getBarcodeByPath(data.id, data.path)
        } else {
          barcode = ((await getBarcode(data.id)) as unknown) as IBarcode
        }

        let project
        if (!(state.projectDetail && barcode.projectId === state.projectDetail.id)) {
          const result = await fetchProjectById(barcode.projectId)
          project = result?.project
        }

        let product
        if (barcode.activationData?.productId) {
          const result = await getProduct(barcode.activationData?.productId)
          product = result
        }
        if (project) {
          getCompiledBarcode(barcode, project)
        }

        commit(SAVE_BARCODE, { barcode: barcode, project, product })
      } catch (err) {
        throw err
      }
    },
    [FETCH_BARCODE_CHILDREN]: async (
      {
        commit,
      }: {
        commit: Function
      },
      barcodeId: string
    ) => {
      try {
        const response = (await getBarcodeChildren(barcodeId)) as Array<IBarcodeList>

        commit(FETCH_BARCODE_CHILDREN, { barcodeChildren: response, barcodeId })
      } catch (err) {
        throw err
      }
    },
    [UNDO_TRACKING]: async (
      {
        commit,
      }: {
        commit: Function
        state: TBarcodeStates
      },
      payload: {
        barcodeId: string
      }
    ) => {
      await undoTracking(payload.barcodeId)
      const barcode = await getBarcode(payload.barcodeId)
      commit(SAVE_BARCODE, barcode)
    },
    [UNDO_LINKING]: async (
      {
        commit,
      }: {
        commit: Function
        state: TBarcodeStates
      },
      payload: {
        barcodeId: string
      }
    ) => {
      await undoTracking(payload.barcodeId)
      const barcode = await getBarcode(payload.barcodeId)
      commit(SAVE_BARCODE, barcode)
    },
  },
}
